import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { SIcon, STable, STooltip } from '@chargebee/sting-react';
import {
  SegmentedControl,
  type SegmentedControlOption,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/SegmentedControl';
import { DatePickerV2 } from '@bbkAdminComponents/reports-filter-picker/reports-filter-picker';
import { PlayMetricChart } from '@bbkAdminComponents/play/play-metrics-chart/play-metric-chart';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { useSelectAllOffers } from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { useShowPlayDemoData } from '@bbkAdminComponents/play/play-context';
import { type RestOffer } from '@bbkAdminUtils/api-client/rest-offers-api';
import {
  reportsSlice,
  ReportType,
  type GetReportV2Args,
  type LifecycleEntityOfferMetricsReport,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { useGetGroupByPeriod } from '@bbkAdminRedux/rtkq/reports.utils';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import { useGetDemoSuffix } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { selectExperiencesAudiencesReportFilter } from '@bbkAdminRedux/rtkq/app-persist/reports-filter';
import { selectFromResultTyped } from '@bbkAdminRedux/redux_utils';
import { formatCurrencyOrNumber } from '@bbkAdminComponents/predictive/customer-risk-level-widget';
import { formatPercents } from '@bbkAdminComponents/settings/plans/components';
import { DotIndicator } from '@bbkAdminComponents/indicators/dot';
import { BrightbackChartHex } from '@bbkAdminComponents/indicators/indicator-colors';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { usePlaysData } from '@bbkAdminComponents/play/use-plays-data';
import { CustomerSnapshotTableV2 } from '@bbkAdminComponents/unified-plays/reporting/reports/customer-snapshot-table-v2';

type Props = {
  playId: string;
};

type MetricName =
  keyof LifecycleEntityOfferMetricsReport['entity_report_metrics'][number]['trends'][number];
type AllowedMetric = Extract<
  MetricName,
  | 'total_customers'
  | 'fulfillments'
  | 'fulfillment_rate'
  | 'engagements'
  | 'accept_rate'
  | 'revenue'
>;
const metricTabs: SegmentedControlOption<AllowedMetric>[] = [
  {
    value: 'total_customers',
    label: 'Customers entered',
  },
  {
    value: 'engagements',
    label: 'Customers engaged',
  },
  {
    value: 'accept_rate',
    label: 'Accept rate',
  },
  {
    value: 'fulfillments',
    label: 'Fulfillments',
  },
  {
    value: 'fulfillment_rate',
    label: 'Fulfillment rate',
  },
  {
    value: 'revenue',
    label: 'Revenue',
  },
];

const PlayOfferReports: React.FC<Props> = ({ playId }) => {
  const appKey = useSelector(selectAppId);
  const isDemoData = useShowPlayDemoData();
  const { startDate: start, endDate: end } = useSelectDatePicker();
  const suffix = useGetDemoSuffix();
  const reportFilters = useSelector(
    selectExperiencesAudiencesReportFilter(suffix)
  );
  const groupByPeriod = useGetGroupByPeriod();
  const [selectedOfferDataPoint, setSelectedOfferDataPoint] =
    useState<string>();

  const requestEntityMetrics: GetReportV2Args['report_type_requests'][number] =
    {
      report_type: ReportType.lifecycle_entity_metrics,
      parameters: {
        entity_type: 'offer',
        group_by: 'offer_id',
        group_by_period: groupByPeriod,
      },
    };
  const argsEntityMetrics: GetReportV2Args = {
    appKey,
    start,
    end,
    report_type_requests: [requestEntityMetrics],
    filters: [
      reportFilters,
      [{ field: 'play_id', operator: 'equals', value: playId }],
    ],
    context: isDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  };

  const optsEntityMetrics = {
    skip: !appKey || !start || !end,
    selectFromResult: selectFromResultTyped<LifecycleEntityOfferMetricsReport>,
  };
  const {
    data: offerEntityMetrics,
    isFetching: isOfferEntityMetricsFetching,
    isLoading: isOfferEntityMetricsLoading,
  } = reportsSlice.endpoints.getReportV2.useQuery(
    argsEntityMetrics,
    optsEntityMetrics
  );

  const { playsList } = usePlaysData();
  const selectedPlayType = playsList.find((x) => x.id === playId)?.play_type;

  const [selectedMetric, setSelectedMetric] = useState<AllowedMetric>(
    metricTabs[0]?.value ?? 'total_customers'
  );
  const offersArgs = {
    appKey,
    context: isDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  };
  const offers = useSelectAllOffers(offersArgs);

  const totalCustomersCount = useMemo(() => {
    if (!selectedOfferDataPoint) return 0;

    return (
      offerEntityMetrics?.entity_report_metrics.find(
        (x) => x.entity_id === selectedOfferDataPoint
      )?.engagements ?? 0
    );
  }, [offerEntityMetrics?.entity_report_metrics, selectedOfferDataPoint]);

  const emptyRevenueTooltip = (
    <STooltip
      label="No revenue to display yet — there haven't been any fulfilled items for this offer."
      width="regular"
      placement="top"
    >
      <span className="tw-underline tw-decoration-dotted tw-underline-offset-2">
        --
      </span>
    </STooltip>
  );

  const hasData =
    offerEntityMetrics?.entity_report_metrics &&
    offerEntityMetrics.entity_report_metrics.length > 0;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <SegmentedControl
          value={selectedMetric}
          onChange={setSelectedMetric}
          options={metricTabs}
          showDividers
        />
        {isDemoData ? (
          <STooltip
            label="Disabled in demo mode."
            width="regular"
            placement="left"
          >
            <div className="tw-inline-block">
              <DatePickerV2 />
            </div>
          </STooltip>
        ) : (
          <DatePickerV2 />
        )}
      </div>
      <PlayMetricChart playId={playId} metric={selectedMetric} />
      <div className="tw-flex tw-flex-col">
        <div className="tw-mb-4 tw-mt-6 tw-flex tw-items-center tw-gap-2">
          <div className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-primary-100 tw-bg-primary-50 tw-p-2">
            <SIcon name="gift" size={18} className="tw-text-primary-600" />
          </div>
          <span className="tw-text-lg tw-font-semibold tw-text-neutral-900">
            Offers
          </span>
        </div>
        <div className="tw-mb-4 tw-text-sm tw-font-light tw-text-neutral-600">
          Select an offer to filter the list of engaged subscribers.
        </div>
        {hasData ||
        isOfferEntityMetricsLoading ||
        isOfferEntityMetricsFetching ? (
          <div className="tw-mb-4 tw-w-full">
            <STable
              loading={
                isOfferEntityMetricsLoading || isOfferEntityMetricsFetching
              }
              loadingRows={5}
              loadingColumns={selectedPlayType === 'ACQUISITION' ? 15 : 11}
            >
              <STable.Head>
                <STable.Row>
                  <STable.HeaderCell className="!tw-text-xs">
                    Offer
                  </STable.HeaderCell>
                  <STable.HeaderCell className="!tw-text-xs">
                    Published
                  </STable.HeaderCell>
                  <STable.HeaderCell className="!tw-text-xs">
                    Cus. Entered
                  </STable.HeaderCell>
                  <STable.HeaderCell className="!tw-text-xs">
                    Cus. Engaged
                  </STable.HeaderCell>
                  <STable.HeaderCell className="!tw-text-xs">
                    Accepts
                  </STable.HeaderCell>
                  <STable.HeaderCell className="!tw-text-xs">
                    Accept Rate
                  </STable.HeaderCell>
                  <STable.HeaderCell className="!tw-text-xs">
                    Fulfillments
                  </STable.HeaderCell>
                  <STable.HeaderCell className="!tw-text-xs">
                    Fulfillment Rate
                  </STable.HeaderCell>
                  <STable.HeaderCell className="!tw-text-xs">
                    Revenue
                  </STable.HeaderCell>
                  {selectedPlayType === 'ACQUISITION' ? (
                    <>
                      <STable.HeaderCell className="!tw-text-xs">
                        Activations
                      </STable.HeaderCell>
                      <STable.HeaderCell className="!tw-text-xs">
                        Trial Conv. Rate
                      </STable.HeaderCell>
                      <STable.HeaderCell className="!tw-text-xs">
                        Checkout Started
                      </STable.HeaderCell>
                      <STable.HeaderCell className="!tw-text-xs">
                        Checkout Completed
                      </STable.HeaderCell>
                      <STable.HeaderCell className="!tw-text-xs">
                        Checkout Conv. Rate
                      </STable.HeaderCell>
                    </>
                  ) : selectedPlayType === 'UPSELL' ? (
                    <STable.HeaderCell className="!tw-text-xs">
                      Expansions
                    </STable.HeaderCell>
                  ) : (
                    <STable.HeaderCell className="!tw-text-xs">
                      Repurchases
                    </STable.HeaderCell>
                  )}
                  <STable.HeaderCell />
                </STable.Row>
              </STable.Head>
              <STable.Body>
                {offerEntityMetrics?.entity_report_metrics.map(
                  (offerDataPoint, idx) => {
                    const encodedTargetId = offerDataPoint.entity_id;
                    let offer:
                      | Pick<RestOffer, 'display_name' | 'published_at'>
                      | undefined;
                    offer = offers.find((o) => o.id === encodedTargetId);
                    // Offer may not be shown in case if it was a control with "No offer" selected in the PlayEditor
                    const wasOfferShown = !offerDataPoint.no_target;
                    if (!wasOfferShown) {
                      offer = { display_name: 'No offer' };
                    }
                    if (!offer) {
                      return null;
                    }
                    return (
                      <STable.Row
                        key={encodedTargetId}
                        className="border-b-neutral-100 [&:not(:last-child)]:border-b tw-cursor-pointer"
                        onClick={() =>
                          setSelectedOfferDataPoint(offerDataPoint.entity_id)
                        }
                      >
                        <STable.Cell>
                          <span className="tw-flex tw-items-center tw-gap-2">
                            <DotIndicator
                              className="tw-shrink-0"
                              dotColor={
                                BrightbackChartHex[
                                  idx % BrightbackChartHex.length
                                ]!.key
                              }
                            />
                            {offer?.display_name}
                          </span>
                        </STable.Cell>
                        <STable.Cell>
                          {offer?.published_at
                            ? moment(offer?.published_at).format('MM/DD/YYYY')
                            : undefined}
                        </STable.Cell>
                        <STable.Cell className="tw-text-center">
                          {formatCurrencyOrNumber(
                            offerDataPoint.total_customers ?? 0
                          )}
                        </STable.Cell>
                        <STable.Cell className="tw-text-center">
                          {formatCurrencyOrNumber(
                            offerDataPoint.engagements ?? 0
                          )}
                        </STable.Cell>
                        <STable.Cell className="tw-text-center">
                          {formatCurrencyOrNumber(offerDataPoint.accepts ?? 0)}
                        </STable.Cell>
                        <STable.Cell className="tw-text-center">
                          {formatPercents(offerDataPoint.accept_rate / 100)}
                        </STable.Cell>
                        <STable.Cell className="tw-text-center">
                          {formatCurrencyOrNumber(
                            offerDataPoint.fulfillments ?? 0
                          )}
                        </STable.Cell>
                        <STable.Cell className="tw-text-center">
                          {formatPercents(
                            offerDataPoint.fulfillment_rate / 100
                          )}
                        </STable.Cell>
                        <STable.Cell className="tw-text-center">
                          {isDefined(offerDataPoint.revenue)
                            ? formatCurrencyOrNumber(
                                offerDataPoint.revenue,
                                true
                              )
                            : emptyRevenueTooltip}
                        </STable.Cell>
                        {selectedPlayType === 'ACQUISITION' ? (
                          <>
                            <STable.Cell className="tw-text-center">
                              {formatCurrencyOrNumber(
                                offerDataPoint.conversions ?? 0
                              )}
                            </STable.Cell>
                            <STable.Cell className="tw-text-center">
                              {formatPercents(
                                (offerDataPoint.trial_conversion_rate ?? 0) /
                                  100
                              )}
                            </STable.Cell>
                            <STable.Cell className="tw-text-center">
                              {offerDataPoint.checkouts_started}
                            </STable.Cell>
                            <STable.Cell className="tw-text-center">
                              {offerDataPoint.checkouts_completed}
                            </STable.Cell>
                            <STable.Cell className="tw-text-center">
                              {formatPercents(
                                (offerDataPoint.checkout_conversion_rate ?? 0) /
                                  100
                              )}
                            </STable.Cell>
                          </>
                        ) : selectedPlayType === 'UPSELL' ? (
                          <STable.Cell className="tw-text-center">
                            {formatCurrencyOrNumber(
                              offerDataPoint.conversions ?? 0
                            )}
                          </STable.Cell>
                        ) : (
                          <STable.Cell className="tw-text-center">
                            {formatCurrencyOrNumber(
                              offerDataPoint.conversions ?? 0
                            )}
                          </STable.Cell>
                        )}
                        <STable.Cell />
                      </STable.Row>
                    );
                  }
                )}
              </STable.Body>
            </STable>
          </div>
        ) : (
          <div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-pb-8 tw-pt-4">
            <span className="tw-text-neutral-600">No offer data found</span>
          </div>
        )}
        {hasData && !selectedOfferDataPoint && (
          <div className="tw-flex tw-h-96 tw-w-full tw-flex-col tw-items-center tw-justify-center">
            <div className="tw-mt-4 tw-w-60 tw-text-center tw-text-xs tw-font-light tw-text-cbrneutral-400">
              Click on an offer to show a snapshot of customers.
            </div>
          </div>
        )}
        {selectedOfferDataPoint && (
          <div className="tw-mb-6 tw-flex tw-w-full tw-flex-col">
            <CustomerSnapshotTableV2
              playId={playId}
              offerId={selectedOfferDataPoint}
              totalCustomersCount={totalCustomersCount}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayOfferReports;
